<template>
    <svg class="colour-wheel" :style="{width: width, height: height}" viewBox="0 0 400 400">
      <g transform="translate(200, 200)">
        <path ref="greenSlice" :d="greenPath" class="colour-wheel-path green-fill" />
        <path ref="yellowSlice" :d="yellowPath" class="colour-wheel-path yellow-fill" />
        <path ref="redSlice" :d="redPath" class="colour-wheel-path red-fill" />
        <path ref="blueSlice" :d="bluePath" class="colour-wheel-path blue-fill" />
        <circle 
          v-for="n in 12"
          :r="segment * n"
          :class="[n % 4 != 0 ? 'light-circle' : 'bold-circle']"
          :key="n"
        />
        <line x1=-199 x2=199 y1=0 y2=0 />
        <line x1=0 x2=0 y1=-199 y2=199 />
        <text :x="4 * segment - 2" y=0 alignment-baseline="hanging" text-anchor="end" class="axis-label">4</text>
        <text :x="8 * segment - 2" y=0 alignment-baseline="hanging" text-anchor="end" class="axis-label">8</text>
        <text :x="12 * segment - 2" y=0 alignment-baseline="hanging" text-anchor="end" class="axis-label">12</text>
        <g transform="translate(170, -170)">
          <circle r=28 class="label-circle green-fill" />
          <foreignObject x=-19 y=-19 width=38 height=38>
            <input 
              ref="green-input" 
              type="text" 
              :value="value.green" 
              class="input green-input"
              @keydown="onKeyDown($event)"
              @input="onInput('green', $event)" 
              @focus="onFocus($event)"
            />
          </foreignObject>
        </g>
        <g transform="translate(170, 170)">
          <circle r=28 class="label-circle yellow-fill" />
          <foreignObject x=-19 y=-19 width=38 height=38>
            <input 
              ref="yellow-input" 
              type="text" 
              :value="value.yellow" 
              class="input yellow-input"
              @keydown="onKeyDown($event)"
              @input="onInput('yellow', $event)"
              @focus="onFocus($event)"
            />
          </foreignObject>
        </g>
        <g transform="translate(-170, 170)">
          <circle r=28 class="label-circle red-fill" />
           <foreignObject x=-19 y=-19 width=38 height=38>
            <input 
              ref="red-input" 
              type="text" 
              :value="value.red" 
              class="input red-input" 
              @keydown="onKeyDown($event)"
              @input="onInput('red', $event)" 
              @focus="onFocus($event)"
            />
          </foreignObject>
       </g>
        <g transform="translate(-170, -170)">
          <circle r=28 class="label-circle blue-fill" />
          <foreignObject x=-19 y=-19 width=38 height=38>
            <input 
              ref="blue-input" 
              type="text" 
              :value="value.blue" 
              class="input blue-input" 
              @keydown="onKeyDown($event)"
              @input="onInput('blue', $event)" 
              @focus="onFocus($event)"
            />
          </foreignObject>
        </g>
      </g>
    </svg>
</template>

<script>
import * as d3 from "d3";
import fitty from "fitty";

export default {
  name: "ColourWheel",
  props: {
    value: Object,
    height: {
      type: String,
      default: "auto"
    },
    width: {
      type: String,
      default: "100%"
    }
  },
  data() {
    return {
      fitties: {},
      greenPath: this.getPath("green", 0.01),
      yellowPath: this.getPath("yellow", 0.01),
      redPath: this.getPath("red", 0.01),
      bluePath: this.getPath("blue", 0.01)
    };
  },
  computed: {
    segment() {
      return 199 / 12;
    }
  },
  watch: {
    value: {
      handler: function() {
        this.updateColourWheel();
        fitty.fitAll();
      },
      deep: true
    }
  },
  methods: {
    updateColourWheel() {
      d3.select(this.$refs.greenSlice)
        .transition()
        .duration(250)
        .attr("d", this.getPath("green", this.value.green))
        .on(
          "end",
          () => (this.greenPath = this.getPath("green", this.value.green))
        );
      d3.select(this.$refs.yellowSlice)
        .transition()
        .duration(250)
        .attr("d", this.getPath("yellow", this.value.yellow))
        .on(
          "end",
          () => (this.yellowPath = this.getPath("yellow", this.value.yellow))
        );
      d3.select(this.$refs.redSlice)
        .transition()
        .duration(250)
        .attr("d", this.getPath("red", this.value.red))
        .on("end", () => (this.redPath = this.getPath("red", this.value.red)));
      d3.select(this.$refs.blueSlice)
        .transition()
        .duration(250)
        .attr("d", this.getPath("blue", this.value.blue))
        .on(
          "end",
          () => (this.bluePath = this.getPath("blue", this.value.blue))
        );
    },
    onInput(property, event) {
      let temp = this.value;
      temp[property] = event.target.value;
      this.$emit("input", temp);
    },
    onFocus(event) {
      event.target.select();
    },
    onKeyDown(event) {
      if (event.keyCode == 8) return;
      if (event.keyCode == 46) return;
      if (!/[0-9\.]/.test(event.key)) {
        event.preventDefault();
      }
    },
    getPath(colour, value) {
      let val = value;
      if (isNaN(val) || val <= 0) val = 0.01;
      if (val > 12) val = 12;
      let temp = d3
        .arc()
        .innerRadius(0)
        .outerRadius(val * this.segment);
      switch (colour) {
        case "green":
          temp.startAngle(0).endAngle(Math.PI * 0.5);
          break;
        case "yellow":
          temp.startAngle(Math.PI * 0.5).endAngle(Math.PI);
          break;
        case "red":
          temp.startAngle(Math.PI).endAngle(Math.PI * 1.5);
          break;
        case "blue":
          temp.startAngle(Math.PI * 1.5).endAngle(Math.PI * 2);
          break;
      }
      return temp();
    }
  },
  mounted() {
    this.fitties.green = fitty(this.$refs["green-input"]);
    this.fitties.yellow = fitty(this.$refs["yellow-input"]);
    this.fitties.red = fitty(this.$refs["red-input"]);
    this.fitties.blue = fitty(this.$refs["blue-input"]);
  }
};
</script>

<style scoped>
.light-circle {
  fill: none;
  stroke: black;
  stroke-width: 0.25;
}
.bold-circle {
  fill: none;
  stroke: black;
  stroke-width: 1.5;
}
line {
  stroke: black;
}
.label-circle {
  stroke: black;
  stroke-width: 3;
}
.input {
  text-align: center;
  font-size: 28px;
  border: none;
  outline: none;
  width: 100%;
  height: 100%;
}
div {
  height: 100%;
}
.axis-label {
  font-size: 10px;
}
svg {
  height: 100vh;
}
</style>
